import { ID } from './id';
import { LinkedItemType } from './linked-item-type';
import { Roles } from './roles';
import { Timestamp } from './timestamp';

export enum ExternalConnectionsTypes {
  Calendar = 'calendar',
}

export enum ExternalConnectionsProviders {
  Google = 'google',
  Outlook = 'outlook',
}

// The backend cannot infer this: User-Agent sniffing only ever recognised iOS.
export enum ExternalConnectionReturnChannel {
  App = 'app',
  Web = 'web',
}

export enum ExternalConnectionsCapabilities {
  ReadEvents = 'read-events',
  WriteEvents = 'write-events',
  ListCalendars = 'list-calendars',
  ReadMailboxSettings = 'read-mailbox-settings',
  ReadContacts = 'read-contacts',
}

// One entry per unique email; a contact with several addresses appears once per address,
// ranked by the provider's relevance.
export type ExternalConnectionContact = {
  name: string | null;
  email: string;
};

export enum ExternalConnectionsStatuses {
  Connected = 'connected',
  Broken = 'broken',
}

export enum ExternalConnectionsBrokenReasons {
  MissingCredentials = 'missing-credentials',
  InvalidGrant = 'invalid-grant',
}

export enum SyncMode {
  Manual = 'manual',
  Automatic = 'automatic',
}

export enum CalendarDetailsVisibility {
  Visible = 'visible',
  Hidden = 'hidden',
}

export type ExternalConnectionGoogleProviderInfo = {
  email: string;
  id: ID;
};

export type CalendarSetting = {
  linkedItemType: LinkedItemType;
  calendarId: ID;
  mode: SyncMode;
  detailsVisibility?: CalendarDetailsVisibility;
  // Only the all-tasks setting carries this: the lower date bound (start of its creation month)
  // below which tasks are not synced. Fixed at creation, preserved across edits.
  syncTasksFrom?: Timestamp | null;
};

// A `calendarSettings` entry lifted out of its connection: the map key becomes `linkedItemId`,
// so the pair (connectionId, linkedItemId) identifies one configured task sync.
export type CalendarSyncEntry = CalendarSetting & {
  connectionId: ID;
  linkedItemId: ID;
};

export type TaskCalendarOverride = {
  synced?: boolean;
  detailsVisibility?: CalendarDetailsVisibility;
};

export type ExternalConnectionGoogleSettings = {
  calendarBlacklist?: ID[] | null;
  syncedGoals?: ID[] | null;
  syncedLifeAreas?: ID[] | null;
  syncAllTasks?: boolean | null;
  calendarSettings?: Record<ID, CalendarSetting> | null;
  taskOverrides?: Record<ID, TaskCalendarOverride> | null;
  // The calendar new events are created on. At most one connection carries it — setting it
  // clears the others — but the write spans documents, so a lost race can leave two.
  defaultCalendar?: ID | null;
};

export type ExternalConnection = {
  createdAt: Timestamp;
  lastUsedAt: Timestamp;
  id: ID;
  provider: ExternalConnectionsProviders;
  providerInfo: ExternalConnectionGoogleProviderInfo;
  roles: Roles;
  type: ExternalConnectionsTypes;
  updatedAt: Timestamp;
  settings?: ExternalConnectionGoogleSettings | null;
  // Backend-owned (client writes are rejected by firestore rules). Absent on connections
  // made before capability tracking — those hold read-only scopes and cannot write events.
  capabilities?: ExternalConnectionsCapabilities[];
  status?: ExternalConnectionsStatuses;
  brokenReason?: ExternalConnectionsBrokenReasons;
};

// The per-connection patch setting one default calendar resolves to, before it reaches the write.
export type DefaultCalendarWrite = {
  connectionId: ID;
  // null, never undefined: the update builder drops undefined values, so a clear must be explicit.
  defaultCalendar: ID | null;
};
